import { StaticVariables } from "./StaticVariables";

export type FireProductBreadcrumbsOptions = {
  /**
   * Dedupes repeat fires for the same username in this page session
   * (e.g. Build click + finish). Does not use localStorage so a later
   * visit can retry once products have finished syncing.
   */
  oncePerBrowser?: boolean;
  /**
   * When set, calls `/api/product-breadcrumbs-by-post` (scoped to these ids).
   * When omitted, calls legacy `/api/product-breadcrumbs` (full null-breadcrumb scan).
   */
  postIds?: string | string[];
};

const firedInSession = new Set<string>();

function normalizePostIds(
  postIds: string | string[] | undefined,
): string[] {
  if (!postIds) return [];
  return (Array.isArray(postIds) ? postIds : [postIds])
    .map((id) => String(id).trim())
    .filter(Boolean);
}

/**
 * Fire-and-forget product breadcrumbs.
 * - with postIds → `/api/product-breadcrumbs-by-post` (insert path)
 * - without → `/api/product-breadcrumbs` (build / backfill)
 * Never throws.
 */
export function fireProductBreadcrumbs(
  username: string | null | undefined,
  options: FireProductBreadcrumbsOptions = {},
): void {
  const uname = String(username || "").trim();
  if (!uname) return;

  const once = options.oncePerBrowser !== false;
  const postIds = normalizePostIds(options.postIds);

  const dedupeKey = postIds.length
    ? `product_breadcrumbs_by_post_v1:${uname}:${postIds.slice().sort().join(",")}`
    : `product_breadcrumbs_fired_v1:${uname}`;

  if (once && firedInSession.has(dedupeKey)) return;
  firedInSession.add(dedupeKey);

  try {
    const API_URL = StaticVariables().API_BASE;
    const endpoint = postIds.length
      ? `${API_URL}/api/product-breadcrumbs-by-post`
      : `${API_URL}/api/product-breadcrumbs`;
    const body: { username: string; postIds?: string[] } = { username: uname };
    if (postIds.length) body.postIds = postIds;

    void fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).catch((err) => {
      console.warn("[fireProductBreadcrumbs] request failed:", err);
    });
  } catch (err) {
    console.warn("[fireProductBreadcrumbs] failed to start:", err);
  }
}
